import { AuthStoreService } from "./auth-store.service";
import { AuthResource } from "./auth.resource";
import { CacheService } from "@/core/common/storage/cache.service";
import { UserDto } from "@/core/features/users/user.dto";
import { get } from "lodash";
import { PermissionEnum } from "@altertec_gparn/lib";
import { LoginResultDto } from "./login-result.dto";
import { TotpSetupResponseDto } from "./totp-setup-response.dto";
import type { AuthenticationResponseJSON, RegistrationResponseJSON } from "@simplewebauthn/browser";
import type { WebauthnRegistrationOptionsDto } from "./webauthn-registration-options.dto";
import type { WebauthnAuthenticationOptionsDto } from "./webauthn-authentication-options.dto";

export class SecurityManager {
  constructor(
    private readonly authStoreService: AuthStoreService,
    private readonly authResource: AuthResource,
    private readonly cacheService: CacheService
  ) {}

  async login(email: string, password: string): Promise<LoginResultDto> {
    const loginResult = await this.authResource.login(email, password);
    if (loginResult.accessToken) {
      await this.authStoreService.storeToken(loginResult.accessToken);
    }

    return loginResult;
  }

  async requestAccessCode(email: string): Promise<void> {
    return this.authResource.requestAccessCode(email);
  }

  async verifyAccessCode(email: string, code: string): Promise<{ passwordExpired: boolean }> {
    const accessTokenDto = await this.authResource.verifyAccessCode(email, code);
    await this.authStoreService.storeToken(accessTokenDto.accessToken);
    return { passwordExpired: accessTokenDto.passwordExpired };
  }

  async setupTotp(ticket: string): Promise<TotpSetupResponseDto> {
    return this.authResource.setupTotp(ticket);
  }

  async getWebauthnRegistrationOptions(ticket: string): Promise<WebauthnRegistrationOptionsDto> {
    return this.authResource.getWebauthnRegistrationOptions(ticket);
  }

  async verifyWebauthnRegistration(
    ticket: string,
    response: RegistrationResponseJSON
  ): Promise<{ passwordExpired: boolean }> {
    const accessTokenDto = await this.authResource.verifyWebauthnRegistration(ticket, response);
    await this.authStoreService.storeToken(accessTokenDto.accessToken);
    return { passwordExpired: accessTokenDto.passwordExpired };
  }

  async confirmTotp(ticket: string, code: string): Promise<{ passwordExpired: boolean }> {
    const accessTokenDto = await this.authResource.confirmTotp(ticket, code);
    await this.authStoreService.storeToken(accessTokenDto.accessToken);
    return { passwordExpired: accessTokenDto.passwordExpired };
  }

  async verifyMfa(ticket: string, code: string): Promise<{ passwordExpired: boolean }> {
    const accessTokenDto = await this.authResource.verifyMfa(ticket, code);
    await this.authStoreService.storeToken(accessTokenDto.accessToken);
    return { passwordExpired: accessTokenDto.passwordExpired };
  }

  async getWebauthnAuthenticationOptions(ticket: string): Promise<WebauthnAuthenticationOptionsDto> {
    return this.authResource.getWebauthnAuthenticationOptions(ticket);
  }

  async verifyWebauthnAuthentication(
    ticket: string,
    response: AuthenticationResponseJSON
  ): Promise<{ passwordExpired: boolean }> {
    const accessTokenDto = await this.authResource.verifyWebauthnAuthentication(ticket, response);
    await this.authStoreService.storeToken(accessTokenDto.accessToken);
    return { passwordExpired: accessTokenDto.passwordExpired };
  }

  async requestEmailFallback(ticket: string): Promise<void> {
    return this.authResource.requestEmailFallback(ticket);
  }

  async verifyEmailFallback(ticket: string, code: string): Promise<{ passwordExpired: boolean }> {
    const accessTokenDto = await this.authResource.verifyEmailFallback(ticket, code);
    await this.authStoreService.storeToken(accessTokenDto.accessToken);
    return { passwordExpired: accessTokenDto.passwordExpired };
  }

  async handleMicrosoftToken(token: string): Promise<void> {
    await this.authStoreService.storeToken(token);
  }

  async logout(): Promise<void> {
    try {
      await this.authResource.logout();
    } catch (error) {
      console.error("Error calling logout endpoint:", error);
    } finally {
      this.cacheService.clear();
      await this.authStoreService.removeToken();
    }
  }

  hasPermission(requiredPermissions: PermissionEnum[], me: UserDto): boolean {
    if (!requiredPermissions) return true;
    const userPermissions = get(me, "role.permissions", []);

    const notPresentPermission = requiredPermissions.find(
      (value) => !userPermissions.some((permission: PermissionEnum) => value === permission)
    );
    return !notPresentPermission;
  }

  hasSectionAccess(sectionAccess: string, me: UserDto): boolean {
    const userPermissions = get(me, "role.permissions", []) ?? [];
    return !!userPermissions.find((value: PermissionEnum) => value.toUpperCase().startsWith(sectionAccess));
  }
}
